// Updated CreateCampaign to include CreateRule modal and dynamic milestone management

import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  CircularProgress,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  IconButton,
  Alert,
  Switch,
  FormControlLabel as MuiFormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { AddCircle, Delete } from "@mui/icons-material";
import Swal from "sweetalert2";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getActivityList } from "../../../services/timesPoint/timesPointActivity";
import { getChannels } from '../../../services/timesPoint/channels';

import {
  createBonusCampaignActivity,
  createBonusCampaignActivityRule,
  getAllCampaignRules,
} from "../../../services/timesPoint/bonusCampaign";

const CreateEditBonusCampaign = ({ onSave, initialData = null }) => {
  const navigate = useNavigate();

  const { id } = useParams();
  const isEditMode = id !== "0";
  const [formData, setFormData] = useState({
    campaignName: "",
    campaignDetail: "",
    pCode: "",
    startDate: moment(),
    startTime: moment(),
    endDate: null,
    endTime: null,
    campaignType: "REGULAR",
    maxCount: 0,
    maxAwardLimit: 0,
    validity: {
      startTime: "",
      endTime: "",
      daysOfWeek: [],
      validHours: "",
    },
    mVerificationChangeNeeded: false,
    ruleId: "",
    active: true,
    productId: "",
    campaignLnkdPageUrl: "",
    bannerImgUrl: "",
    referenceActList: [],
    bonusActivityName: "",
    bonusActivityCode: "",
    selectedActivities: [
      {
        spacode: "",
        platform: [],
        aCount: "",
        type: "",
      },
    ],
    activityEligibilityRule: {
      requiredActivities: [
        { spacode: "", minCount: 0, maxCount: 0 }
      ]
    },
    action: "",
    ult: 0,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openRuleDialog, setOpenRuleDialog] = useState(false);
  const [rules, setRules] = useState([]);
  const [selectedRule, setSelectedRule] = useState("");
  const [referenceActivities, setReferenceActivities] = useState([]);
  const [bonusActivities, setBonusActivities] = useState([]);
  const [channels, setChannels] = useState([]);

  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [ruleTabValue, setRuleTabValue] = useState(0);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        startDate: moment(initialData.startDate),
        endDate: moment(initialData.endDate),
      });
    }
    fetchRules();
    fetchActivity();
    fetchChannels();
  }, [initialData]);

  const fetchRules = async () => {
    const allCampaignRulesRes = await getAllCampaignRules();
    const { status, data } = allCampaignRulesRes;
    if (status === "SUCCESS") {
      setRules(data);
      return;
    }
  };

  const fetchActivity = async () => {
    const allActivities = await getActivityList();
    const { success, activities } = allActivities;
    if (success === true) {
      setReferenceActivities(activities);
      setBonusActivities(activities)
      return;
    }
  };

  const fetchChannels = async () => {
    try {
      const response = await getChannels();
      if (response.data && response.data.status === 'SUCCESS') {
        setChannels(response.data.data);
      }
    } catch (error) {
      setChannels([]);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleReferenceActivityChange = (activity) => {
    setFormData((prev) => {
      const exists = prev.referenceActList.some(
        (act) => act.actId === activity.actId
      );
      if (exists) {
        return {
          ...prev,
          referenceActList: prev.referenceActList.filter(
            (act) => act.actId !== activity.actId
          ),
        };
      } else {
        return {
          ...prev,
          referenceActList: [...prev.referenceActList, activity],
        };
      }
    });
  };

  const handleActivityChange = (value, index, key) => {
    const updated = [...formData.selectedActivities];
    updated[index][key] = value;
    setFormData((prev) => ({
      ...prev,
      selectedActivities: updated,
    }));
  };

  const addActivity = () => {
    setFormData((prev) => ({
      ...prev,
      selectedActivities: [
        ...prev.selectedActivities,
        {
          spacode: "",
          platform: [],
          aCount: "",
          type: "REWARD",
        },
      ],
    }));
  };

  const removeActivity = (index) => {
    const updated = [...formData.selectedActivities];
    updated.splice(index, 1);
    setFormData((prev) => ({
      ...prev,
      selectedActivities: updated,
    }));
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.campaignName) errors.push("Campaign Name is required");
    if (!formData.pCode) errors.push("Channel is required");
    if (!formData.startDate) errors.push("Start Date is required");
    if (!formData.endDate) errors.push("End Date is required");
   
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (errors.length > 0) {
      setError(errors.join(", "));
      return;
    }

    setLoading(true);
    setError(null);
    const postData = {
      ...formData,
      startDate: formData.startDate.unix(),
      endDate: formData.endDate.unix(),
      bonusActivityName: formData.bonusActivityName,
      bonusActivityCode: formData.bonusActivityCode,
    };
    const createBonusCampaignActivityRes = await createBonusCampaignActivity(
      postData
    );
    const { status, message } = createBonusCampaignActivityRes || {};
    setLoading(false);
    if (status === "SUCCESS") {
      Swal.fire({
        title: "Success",
        text: message,
        icon: "success",
        confirmButtonText: "Ok",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/app/list-bonus-campaign");
        }
      });
      return;
    }
    Swal.fire({
      title: "Oops",
      text: message,
      icon: "warning",
      confirmButtonText: "Ok",
    });
  };

  const renderListofActivities = () =>
    formData.selectedActivities.map((activity, index) => (
      <Box key={index} display="flex" alignItems="center" gap={2} mb={2}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id={`activity-${index}-label`}>
            Select Activity
          </InputLabel>
          <Select
            labelId={`activity-${index}-label`}
            label="Select Activity"
            value={activity.spacode}
            onChange={(e) =>
              handleActivityChange(e.target.value, index, "spacode")
            }
            required
          >
            {referenceActivities.map((refAct) => (
              <MenuItem key={refAct.spacode} value={refAct.spacode}>
                {refAct.desc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id={`platform-${index}-label`}>Platform</InputLabel>
          <Select
            labelId={`platform-${index}-label`}
            value={activity.platform}
            label="Platform"
            onChange={(e) =>
              handleActivityChange(e.target.value, index, "platform")
            }
            multiple
            required
          >
            <MenuItem value="web">Web</MenuItem>
            <MenuItem value="android">Android</MenuItem>
            <MenuItem value="ios">iOS</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Activity Count"
          type="number"
          value={activity.aCount}
          onChange={(e) =>
            handleActivityChange(e.target.value, index, "aCount")
          }
          sx={{ width: 150 }}
          required
        />

        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel id={`type-${index}-label`}>Type</InputLabel>
          <Select
            labelId={`type-${index}-label`}
            value={activity.type || ""}
            label="Type"
            onChange={(e) => handleActivityChange(e.target.value, index, "type")}
            required
          >
            <MenuItem value="REWARD">REWARD</MenuItem>
            <MenuItem value="ACTIVATION">ACTIVATION</MenuItem>
          </Select>
        </FormControl>

        {index > 0 && (
          <IconButton onClick={() => removeActivity(index)}>
            <Delete />
          </IconButton>
        )}
      </Box>
    ));

  const handleRuleChange = (event) => {
    setSelectedRule(event.target.value);
    handleChange("ruleId", event.target.value);
  };

  const handleRuleTabChange = (event, newValue) => {
    setRuleTabValue(newValue);
  };

  const handleCreateRule = (newRule) => {
    setRules((prev) => [...prev, newRule]);
    setSelectedRule(newRule.ruleCode);
    handleChange("ruleId", newRule.ruleCode);
    setRuleDialogOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ p: 4 }}>
        {loading && <CircularProgress />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Campaign created successfully!
          </Alert>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "25px",
            marginTop: "20px",
            gap: "16px",
          }}
        >
          <Link to={"/app/list-bonus-campaign"}>
            <ArrowBackIcon
              fontSize="large"
              l
              color="secondary"
              sx={{ background: "#040721", p: 1, borderRadius: "50%" }}
            />
          </Link>
          <Typography
            variant="h4"
            component="h5"
            fontWeight={"bold"}
            color={"#3d3d3d"}
          >
            Create a Campaign
          </Typography>
        </div>
        <TextField
          fullWidth
          label="Campaign Name"
          value={formData.campaignName}
          onChange={(e) => handleChange("campaignName", e.target.value)}
          sx={{ mb: 2 }}
          required
        />

        <TextField
          fullWidth
          label="Sub Title"
          value={formData.campaignDetail}
          onChange={(e) => handleChange("campaignDetail", e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="channel-label">Select Channel</InputLabel>
          <Select
            labelId="channel-label"
            label="Select Channel"
            value={formData.pCode}
            onChange={(e) => handleChange("pCode", e.target.value)}
            required
          >
            {channels.map((channel) => (
              <MenuItem key={channel.pCode || channel.pname || channel._id} value={channel._id}>
                {channel.displayName || channel.pname || channel.pCode}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="bonus-activity-label">Bonus Activity</InputLabel>
          <Select
            labelId="bonus-activity-label"
            label="Bonus Activity"
            value={formData.bonusActivityCode || ""}
            onChange={(e) => {
              const selected = bonusActivities.find(a => a.acode === e.target.value);
              handleChange("bonusActivityName", selected ? selected.aname : "");
              handleChange("bonusActivityCode", selected ? selected.acode : "");
            }}
            required
          >
            {bonusActivities.map((activity) => (
              <MenuItem key={activity.acode} value={activity.acode}>
                {activity.desc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Select reference activities for this campaign
          </Typography>
          {renderListofActivities()}
          {(formData.campaignType === "REGULAR" ||
            formData.campaignType === "AUTO_REDEEMABLE") && (
            <Button
              startIcon={<AddCircle />}
              onClick={addActivity}
              sx={{ mt: 2 }}
            >
              Add more
            </Button>
          )}
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Required Activities (Eligibility Rule)
          </Typography>
          {formData.activityEligibilityRule.requiredActivities.map((activity, idx) => (
            <Box key={idx} display="flex" alignItems="center" gap={2} mb={6}>
              <FormControl sx={{ minWidth: 200 }}>
              <InputLabel >
                Select Eligibility Activity
              </InputLabel>
              <Select
                label="Select Eligibility Activity"
                value={activity.spacode}
                onChange={e => {
                  const updated = [...formData.activityEligibilityRule.requiredActivities];
                  updated[idx].spacode = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    activityEligibilityRule: { ...prev.activityEligibilityRule, requiredActivities: updated }
                  }));
                }}
                required
              >
                {referenceActivities.map((refAct) => (
                  <MenuItem key={refAct.spacode} value={refAct.spacode}>
                    {refAct.desc}
                  </MenuItem>
                ))}
              </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>
              <TextField
                label="Min Count"
                type="number"
                value={activity.minCount}
                onChange={e => {
                  const updated = [...formData.activityEligibilityRule.requiredActivities];
                  updated[idx].minCount = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    activityEligibilityRule: { ...prev.activityEligibilityRule, requiredActivities: updated }
                  }));
                }}
                sx={{ width: 120 }}
                required
              />
              </FormControl>
              <FormControl sx={{ minWidth: 200 }}>  
              <TextField
                label="Max Count"
                type="number"
                value={activity.maxCount}
                onChange={e => {
                  const updated = [...formData.activityEligibilityRule.requiredActivities];
                  updated[idx].maxCount = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    activityEligibilityRule: { ...prev.activityEligibilityRule, requiredActivities: updated }
                  }));
                }}
                sx={{ width: 120 }}
                required
              />
              </FormControl>
              <IconButton
                onClick={() => {
                  const updated = [...formData.activityEligibilityRule.requiredActivities];
                  updated.splice(idx, 1);
                  setFormData(prev => ({
                    ...prev,
                    activityEligibilityRule: { ...prev.activityEligibilityRule, requiredActivities: updated }
                  }));
                }}
                disabled={formData.activityEligibilityRule.requiredActivities.length === 1}
              >
                <Delete />
              </IconButton>
            </Box>
          ))}
          <Button
            startIcon={<AddCircle />}
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                activityEligibilityRule: {
                  ...prev.activityEligibilityRule,
                  requiredActivities: [
                    ...prev.activityEligibilityRule.requiredActivities,
                    { spacode: "", minCount: 0, maxCount: 0 }
                  ]
                }
              }));
            }}
            sx={{ mt: 1 }}
          >
            Add Required Activity
          </Button>
        </Box>

        <Typography>Campaign Type</Typography>
        <RadioGroup
          row
          value={formData.campaignType}
          onChange={(e) => handleChange("campaignType", e.target.value)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel
            value="REGULAR"
            control={<Radio />}
            label="Regular"
          />
          <FormControlLabel
            value="RECURRING_ACCUMULATIVE"
            control={<Radio />}
            label="Recurring Accumulative"
          />
          <FormControlLabel
            value="NONRECURRING_ACCUMULATIVE"
            control={<Radio />}
            label="Non Recurring Accumulative"
          />
          <FormControlLabel
            value="RECURRING_DISCONTINUES"
            control={<Radio />}
            label="Recurring Discontinuous"
          />
          <FormControlLabel
            value="AUTO_REDEEMABLE"
            control={<Radio />}
            label="Auto Redemption"
          />
        </RadioGroup>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <DatePicker
            label="Start Date"
            value={formData.startDate}
            onChange={(date) => handleChange("startDate", date)}
            renderInput={(params) => (
              <TextField fullWidth {...params} required />
            )}
          />
          <TimePicker
            label="Start Time"
            value={formData.startTime}
            onChange={(time) => handleChange("startTime", time)}
            renderInput={(params) => (
              <TextField fullWidth {...params} required />
            )}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <DatePicker
            label="End Date"
            value={formData.endDate}
            onChange={(date) => handleChange("endDate", date)}
            renderInput={(params) => (
              <TextField fullWidth {...params} required />
            )}
          />
          <TimePicker
            label="End Time"
            value={formData.endTime}
            onChange={(time) => handleChange("endTime", time)}
            renderInput={(params) => (
              <TextField fullWidth {...params} required />
            )}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="Action URL"
            value={formData.action}
            onChange={e => handleChange("action", e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="User Limit Time(mins)"
            type="number"
            value={formData.ult}
            onChange={e => handleChange("ult", parseInt(e.target.value))}
            sx={{ mb: 2 }}
            required
          />
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="rule-label">Select Rule</InputLabel>
          <Select
            labelId="rule-label"
            label="Select Rule"
            value={selectedRule}
            onChange={handleRuleChange}
            required
          >
            {rules.map((rule) => (
              <MenuItem key={rule._id} value={rule._id}>
                {rule.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          onClick={() => setRuleDialogOpen(true)}
          sx={{ mb: 2 }}
        >
          Create New Rule
        </Button>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            label="Max Count"
            type="number"
            value={formData.maxCount}
            onChange={(e) => handleChange("maxCount", parseInt(e.target.value))}
            required
          />
          <TextField
            fullWidth
            label="Max Award Limit"
            type="number"
            value={formData.maxAwardLimit}
            onChange={(e) =>
              handleChange("maxAwardLimit", parseInt(e.target.value))
            }
            required
          />
        </Box>

       

        <Box sx={{ mt: 4 }}>
          {/* <Button
            variant="contained"
            onClick={() => setOpenRuleDialog(true)}
            sx={{ mr: 2 }}
          >
            Create Rule
          </Button> */}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            Create Campaign
          </Button>
        </Box>

        <Dialog open={openRuleDialog} onClose={() => setOpenRuleDialog(false)}>
          <DialogTitle>Create Rule</DialogTitle>
          <DialogContent>
            <Typography>Rule creation form goes here.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRuleDialog(false)}>Cancel</Button>
            <Button variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Confirm Submission</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to submit?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={ruleDialogOpen}
          onClose={() => setRuleDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create Rule</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <CreateRule
                onSave={handleCreateRule}
                campaignType={formData.campaignType}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRuleDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

const CreateRule = ({ onSave, campaignType = "REGULAR" }) => {
  const [ruleData, setRuleData] = useState({
    type: "percentage",
    val: "",
    desc: "",
    app_on: "flat",
    maxval: "",
    name: "",
    dname: ""
  });

  const [errors, setErrors] = useState({
    ruleNameErr: "",
    ruleWeightErr: "",
    ruleMaxErr: "",
  });

  const handleChange = (field, value) => {
    setRuleData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when field is updated
    if (errors[`${field}Err`]) {
      setErrors((prev) => ({
        ...prev,
        [`${field}Err`]: "",
      }));
    }
  };

  const validateRule = () => {
    const newErrors = {};
    if (!ruleData.name) newErrors.ruleNameErr = "This field is required!";
    if (!ruleData.val) newErrors.ruleWeightErr = "This field is required!";
    if (!ruleData.maxval) newErrors.ruleMaxErr = "This field is required!";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateRule = async () => {
    if (!validateRule()) return;
    const postData = {
      type: ruleData.type,
      val: ruleData.val,
      desc: ruleData.desc,
      app_on: ruleData.app_on,
      maxval: ruleData.maxval,
      name: ruleData.name,
      dname: ruleData.dname
    };
    const createBonusCampaignRuleRes = await createBonusCampaignActivityRule(postData);
    const { status, data } = createBonusCampaignRuleRes || {};
    if (status === "SUCCESS") {
      onSave(data);
    }
    setErrors((prev) => ({
      ...prev,
      submitErr: "Failed to create rule",
    }));
  };

  return (
    <Box sx={{ p: 2 }}>
      {errors.submitErr && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.submitErr}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Rule Name"
        value={ruleData.name}
        onChange={(e) => handleChange("name", e.target.value)}
        error={!!errors.ruleNameErr}
        helperText={errors.ruleNameErr}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Display Name"
        value={ruleData.dname}
        onChange={(e) => handleChange("dname", e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Description"
        value={ruleData.desc}
        onChange={(e) => handleChange("desc", e.target.value)}
        sx={{ mb: 2 }}
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Type</InputLabel>
        <Select
          label="Type"
          value={ruleData.type}
          onChange={(e) => handleChange("type", e.target.value)}
        >
          <MenuItem value="percentage">Percentage</MenuItem>
          <MenuItem value="flat">Flat</MenuItem>
        </Select>
      </FormControl>
      <TextField
        fullWidth
        label="Value"
        value={ruleData.val}
        onChange={(e) => handleChange("val", e.target.value)}
        error={!!errors.ruleWeightErr}
        helperText={errors.ruleWeightErr}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label="Applicable On"
        select
        value={ruleData.app_on}
        onChange={(e) => handleChange("app_on", e.target.value)}
        sx={{ mb: 2 }}
      >
        <MenuItem value="flat">Flat</MenuItem>
        <MenuItem value="bonus">Bonus</MenuItem>
      </TextField>
      <TextField
        fullWidth
        label="Max Value"
        value={ruleData.maxval}
        onChange={(e) => handleChange("maxval", e.target.value)}
        error={!!errors.ruleMaxErr}
        helperText={errors.ruleMaxErr}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="contained" onClick={handleCreateRule}>
          Create Rule
        </Button>
      </Box>
    </Box>
  );
};

export default CreateEditBonusCampaign;
